/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Ticket, TicketFields, ActualPhase, Side } from '@touchplan/common';
import { localList, Role } from '@touchplan/common';

import { PromisePin } from '@touchplan/common';

import { IPreviousAssignedProjectMember } from '@touchplan/common';
import { PersistentEditData } from 'src/app/modules/ticket-edit/interfaces/PresistentEditData';

import { CONSTRAINT_ACCEPTANCE_STATE } from '@touchplan/common';
import { Location } from '@touchplan/common';
import { TicketType } from '@touchplan/common';

export enum TicketDropdownTypes {
	CONTRAINT_ASSIGNED = 'constraint_assigned',
	CONSTRAINT_ACCEPTED = 'constraint_accepted',
}

export type CombinedTicketTypes = TicketType | TicketDropdownTypes;

export interface TicketCounts {
	[TicketType.Task]: number;
	[TicketType.Constraint]: number;
	[TicketType.Milestone]: number;
	[TicketDropdownTypes.CONTRAINT_ASSIGNED]: number;
	[TicketDropdownTypes.CONSTRAINT_ACCEPTED]: number;
}

interface TicketTypeLists {
	[TicketType.Task]: Map<string, Ticket>;
	[TicketType.Constraint]: Map<string, Ticket>;
	[TicketType.Milestone]: Map<string, Ticket>;
	[TicketDropdownTypes.CONTRAINT_ASSIGNED]: Map<string, Ticket>;
	[TicketDropdownTypes.CONSTRAINT_ACCEPTED]: Map<string, Ticket>;
}

//few tactics here, where subset is the set of data that is actually interesting to edit
// - copy subset, interface subset
// - copy all, interface subset <--------- start here
// - copy all, interface all
// this means that the interface should be updated when
export interface EditCombinedData extends TicketFields {
	actualPhase?: ActualPhase;
	activityName?: string;
	// actualFinish?: string,
	assignedProjectMemberId?: string;
	assignedProjectMemberRoleId?: string;
	assignedProjectMemberRole?: Role;
	// createdAt?: string,
	creatorId?: string;
	// crewSize?: number,
	constraintState?: CONSTRAINT_ACCEPTANCE_STATE;
	dateRequested?: string;
	durationDays?: number;
	// hasPredecessorOutOfOrder?: boolean,
	// hasScheduleViolations?: boolean,
	height?: number;
	// id?: string,
	// isActive?: boolean,
	isActualized?: boolean; //local
	isAccepted?: boolean;
	// isBlocked?: boolean,
	// isCyclic?: boolean,
	// isLateConstraintRequested?: boolean,
	// isLateTicketFinish?: boolean,
	// isOutOfOrder?: boolean,
	lastPlannedFinish?: string;
	lastPlannedStart?: string;
	left?: number;
	// legacyDatePlanned?: string,
	// legacyDateRequested?: string,
	liveAssignedProjectMemberId?: string;
	liveCrewSize?: number;
	liveDurationDays?: number;
	livePromisePeriodStack?: PromisePin[];
	liveLastPromise?: PromisePin;
	// liveFinishX?: number,
	liveDateRange?: string[];
	liveExceptionDays?: Map<string, boolean>;
	liveFinish?: string;
	liveRole?: Role;
	liveStart?: string;
	needsVariance?: boolean;
	notes?: string;
	location?: Location;
	locationId?: string;
	// plannedFinish?: string,
	// plannedStart?: string,
	// predecessorOutOfOrderCount?: number,
	predecessors?: any; //todo, do better
	previousAssignedProjectMembers?: IPreviousAssignedProjectMember[];
	priorityId?: string;
	procoreRfiId?: string;
	promisePeriodPosition?: string;
	promisePeriodStack?: any; //todo, do better
	pullColumn?: number;
	requestedByProjectMemberId?: string;
	requestedByProjectMemberRoleId?: string;
	requestedByProjectMemberRole?: Role;
	responsibleProjectMemberId?: string;
	responsibleProjectMemberRoleId?: string;
	roleId?: string;
	// side?: Side,
	// tminusFinish?: number,
	top?: number;
	// type?: string,
	// userOrder?: number,
	width?: number;
	workingDays?; //todo, do better

	externalScheduleId?: string;
	externalScheduleFinishDate?: string;

	wbsCode?: string;
	ticketsActualPhases?: Map<ActualPhase, boolean>;

	[key: string]: any;
}
//the list of actually relevant keys
//currently just used to determine equivalency, most likely this will need to be dramatically enhanced for validation
const editKeys = [
	'actualPhase',
	'actualPhaseOverride',
	'assignedProjectMemberId',
	'assignedProjectMemberRoleId',
	'requestedByProjectMemberId',
	'requestedByProjectMemberRoleId',
	'responsibleProjectMemberId',
	'responsibleProjectMemberRoleId',
	'activityName',
	'roleId',
	'locationId',
	'liveCrewSize',
	'durationDays',
	'procoreRfiId',
	'liveWorkingDays',
	'liveLastPromise',
	'livePromisePeriodStack',
	'liveDateRange',
	'liveAssignedProjectMemberId',
	'needsVariance',
	'dateRequested',
	'externalScheduleId',
	'wbsCode',
	'milestoneType',
	// "livePromisePeriodStack"
];

function safeType(type: CombinedTicketTypes | string): CombinedTicketTypes {
	return (type as CombinedTicketTypes) || TicketType.Task;
}

//---------------------------------------
// Actual class start
//---------------------------------------
export class EditTicketsCombined {
	public data: EditCombinedData;
	public ticketJustAdded = false;
	public typeCounts: TicketCounts;
	public keyMetadata: Map<string, 'multiple' | 'cleared'>; //todo - create interface/ type for this
	public databaseSide: Side;
	public selectedType;
	public messageString: string;
	public originalList: Map<string, Ticket>;
	/** true when the selected tickets live in the ticketList, false when floating */
	public ticketsInPlanRegion = true;
	/** the list of the selectedType */
	public selectedList: Map<string, Ticket>;
	private typeLists: TicketTypeLists;

	public workingDaysHoldEndDate = false;

	get isMulti() {
		return this.originalList && this.originalList.size > 1;
	}

	public lastUpdateInternal = false;

	/** Use to compare if two instances of EditTicketsCombined are actually different */
	public static combinedChanged(original: EditTicketsCombined, newOne: EditTicketsCombined) {
		const differences = [];
		editKeys.forEach((key) => {
			if (original.data[key] !== newOne.data[key]) {
				differences.push(key);
			}
			if (original.keyMetadata.get(key) !== newOne.keyMetadata.get(key)) {
				differences.push(key);
			}
		});
		return differences.length ? differences : null;
	}

	public static EMPTY() {
		return new EditTicketsCombined(TicketType.Task, new Map(), {});
	}

	constructor(type: CombinedTicketTypes, list: Map<string, Ticket>, persistentEditData: PersistentEditData) {
		this.update(type, list, persistentEditData);
	}

	public update(
		type: CombinedTicketTypes,
		list: Map<string, Ticket>,
		persistentEditData: PersistentEditData
	) {
		if (!list) {
			return;
		}
		const combinedData: EditCombinedData = {};
		//TODO - this one is probably not necessary since adding typeLists
		const typeCount = {
			[TicketType.Task]: 0,
			[TicketType.Constraint]: 0,
			[TicketType.Milestone]: 0,
			[TicketDropdownTypes.CONSTRAINT_ACCEPTED]: 0,
			[TicketDropdownTypes.CONTRAINT_ASSIGNED]: 0,
		};
		const typeLists = {
			[TicketType.Task]: new Map<string, Ticket>(),
			[TicketType.Constraint]: new Map<string, Ticket>(),
			[TicketType.Milestone]: new Map<string, Ticket>(),
			[TicketDropdownTypes.CONSTRAINT_ACCEPTED]: new Map<string, Ticket>(),
			[TicketDropdownTypes.CONTRAINT_ASSIGNED]: new Map<string, Ticket>(),
		};
		list.forEach((t) => {
			//special handling for constraints
			let decoratedType: CombinedTicketTypes;
			if (t.view.type === TicketType.Constraint) {
				if (t.view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED) {
					decoratedType = TicketDropdownTypes.CONSTRAINT_ACCEPTED;
				} else if (t.view.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ASSIGNED) {
					decoratedType = TicketDropdownTypes.CONTRAINT_ASSIGNED;
				} else {
					decoratedType = TicketType.Constraint;
				}
			} else {
				decoratedType = t.view.type as TicketType;
			}

			typeCount[safeType(decoratedType)]++;
			typeLists[safeType(decoratedType)].set(t.$id, t);
		});
		if (typeCount[type] === 0) {
			type = this.calcNewType(type, typeCount);
		}
		//now the type is known, don't edit it more
		this.selectedList = typeLists[type];

		//store special information about the keys
		const keyMetadata = new Map();

		/*
		 * Stores the actual phases included in a group of tickets.
		 *
		 * Important: Since 'ticketsActualPhases' is not added to 'keyMetadata',
		 * it cannot be used to compare whether two instances of 'EditTicketsCombined' are different.
		 * However, since 'actualPhase' is included and 'ticketsActualPhases' is derived from 'actualPhase',
		 * using 'ticketsActualPhases' for comparison is likely unnecessary.
		 */
		combinedData.ticketsActualPhases = new Map();

		//real pass
		let ticketIdx = 0;
		this.selectedList.forEach((ticket) => {
			//can't edit the date in multiple so this should be safe for now
			this.databaseSide = ticket.database.side;

			localList.forEach((key) => {
				//need to add special handling for checking if values are the same
				// the type of check will depend on the property, things like roles will be checked based on their parents, etc
				// if(key === "locationId"){ debugger; }
				//all tickets the same
				if (key === 'actualPhase') {
					combinedData.ticketsActualPhases.set(ticket.view[key], true);
				}
				if (ticketIdx === 0 || combinedData[key] === ticket.view[key]) {
					combinedData[key] = ticket.view[key];
					keyMetadata.delete(key);
				} else {
					if (key === 'actualPhase') {
						combinedData[key] = ActualPhase.multiple;
					}
					//special multiple handling for needVariance, should be true if _any_ is true
					else if (key === 'needsVariance') {
						combinedData[key] = combinedData[key] || ticket.view[key];
					} else {
						combinedData[key] = null;
					}
					keyMetadata.set(key, 'multiple');
				}

				// if(keyMetadata.get(key) !== "multiple" && (!combinedData[key] || combinedData[key] === ticket.view[key])){
				// 	combinedData[key] = ticket.view[key];
				// 	keyMetadata.delete(key);
				// }
				// else{
				// 	if(key === "actualPhase"){ combinedData[key] = ActualPhase.multiple; }
				// 	else{ combinedData[key] = null; }
				// 	keyMetadata.set(key, "multiple");
				// }
			});
			ticketIdx++;
		});

		//DISABLED
		// Object.keys(combinedData).forEach(key => {
		// 	//check for resets in cleared
		// 	if(persistentEditData.clearedKeys.has(key)){
		// 		keyMetadata.set(key, 'cleared');
		// 	}
		// })

		//ensure this is always defined
		if (!combinedData.liveExceptionDays) {
			combinedData.liveExceptionDays = new Map();
		}

		this.keyMetadata = keyMetadata;
		//derived data (based only on combined data)
		//current state, can be null, an array with 1 value, or an array with 2 values based on what's there
		const dateRange = [];
		if (combinedData.liveStart) {
			dateRange.push(combinedData.liveStart);
		}
		if (combinedData.liveFinish) {
			dateRange.push(combinedData.liveFinish);
		}
		if (dateRange.length) {
			combinedData.liveDateRange = dateRange;
		}

		this.data = combinedData;
		this.typeCounts = typeCount;
		this.selectedType = type;
		this.originalList = list;

		// this.updateMessageString();

		let ticketJustAdded = false;
		this.selectedList.forEach((t) => (ticketJustAdded = ticketJustAdded || t.ticketJustAdded));
		this.ticketJustAdded = ticketJustAdded;
	}

	public setMessageString(newMessage: string): void {
		this.messageString = newMessage;
	}

	// updateMessageString(){
	// 	if(!this.data){return;}
	//
	// }

	public calcNewType(currentType: CombinedTicketTypes, counts: TicketCounts) {
		//unchanged
		if (counts[currentType]) {
			return currentType;
		}

		//special ordering for "previously constraint types" to catch fun edge cases like TP-453
		// necessary as a side effect of constraints having subtypes [[CombinedTicketTypes]] which can be changed dynamically during edit
		// this allows constraints to first fall through other constraint types before changing to the standard default of task
		else if (this.isAnyConstraint(currentType)) {
			let newConstraintType: CombinedTicketTypes;
			const matched = [
				TicketDropdownTypes.CONTRAINT_ASSIGNED,
				TicketDropdownTypes.CONSTRAINT_ACCEPTED,
				TicketType.Constraint,
			].some((type) => {
				if (counts[type]) {
					newConstraintType = type;
					return true;
				}
			});
			if (matched) {
				return newConstraintType;
			} //else fallback to default
		}

		//normal calculation
		if (counts[TicketType.Task]) {
			return TicketType.Task;
		} else if (counts[TicketType.Constraint]) {
			return TicketType.Constraint;
		} else if (counts[TicketDropdownTypes.CONTRAINT_ASSIGNED]) {
			return TicketDropdownTypes.CONTRAINT_ASSIGNED;
		} else if (counts[TicketDropdownTypes.CONSTRAINT_ACCEPTED]) {
			return TicketDropdownTypes.CONSTRAINT_ACCEPTED;
		} else {
			return TicketType.Milestone;
		}
	}

	public isAnyConstraint(type: CombinedTicketTypes) {
		return (
			type === TicketType.Constraint ||
			type === TicketDropdownTypes.CONSTRAINT_ACCEPTED ||
			type === TicketDropdownTypes.CONTRAINT_ASSIGNED
		);
	}
}
